import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { User } from '../../../auth/models';
import { Validators } from '../../../common/forms';
import { Contract, ContractSupplier } from '../../models';
import { AWARD_SELF_SUPPLY_OPTIONS } from '../../models/offer';
import { TenderContractExtendRoundService } from '../../services';

@Component({
    selector: 'jft-edit-contract-conditions-btn',
    templateUrl: './edit-contract-conditions-btn.component.html',
    styleUrls: ['./edit-contract-conditions-btn.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditContractConditionsBtnComponent implements OnInit, OnDestroy {
    @Input()
    public offer: ContractSupplier;
    @Input()
    public contract: Contract;
    @Input()
    public title: string;
    @Input()
    public popoverPlacement: string = 'left';
    @Input()
    public loadingAward: boolean = false;
    @Input()
    public class: string = 'btn btn-outline-primary';

    @Output()
    public onEdit = new EventEmitter();

    public AWARD_SELF_SUPPLY_OPTIONS = AWARD_SELF_SUPPLY_OPTIONS;
    public docsUrl = environment.docsUrl;
    public form: UntypedFormGroup;
    private destroy$ = new Subject<void>();

    constructor(
        protected service: TenderContractExtendRoundService,
        private modalService: NgbModal,
        public user: User,
    ) {
    }

    public ngOnInit() {
        this.form = this.createForm(this.offer, this.contract);

        this.form.controls.volume.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.form.controls.percent_awarded.patchValue(((this.toNumericValue(value) / this.contract.volume) * 100)
                .toFixed(0), {
                emitEvent: false,
                onlySelf: true
            });
        });

        this.form.controls.percent_awarded.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.form.controls.volume.patchValue(this.formatVolume((this.toNumericValue(value) * this.contract.volume) / 100), {
                emitEvent: false,
                onlySelf: true
            });
        });
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public createForm(offer: ContractSupplier, contract: Contract): UntypedFormGroup {
        // Current contracts are imported from a past tender, so their dates come from the contract
        // the offer was made against, not the one being tendered now.
        const offerContract = offer.round?.contract || contract;
        return new UntypedFormGroup({
            percent_awarded: new UntypedFormControl(
                this.offer.awardedPercent || 100,
                [Validators.integer, Validators.required, Validators.min(1), Validators.max(100)]),
            volume: new UntypedFormControl(
                this.formatVolume(this.contract.volume),
                [Validators.required]),
            is_award_self_supply: new UntypedFormControl(false, []),
            award_self_supply: new UntypedFormControl(this.offer.awardSelfSupply ? this.offer.awardSelfSupply : '', []),
            date_contract_start: new UntypedFormControl(offer.bid.dateContractStart ? offer.bid.dateContractStart : offerContract.dateStart, []),
            date_contract_end: new UntypedFormControl(offer.bid.dateContractEnd ? offer.bid.dateContractEnd : offerContract.dateEnd, []),
        });
    }

    public openModal(content: any) {
        const modalRef = this.modalService.open(content, {
            size: 'lg',
            backdrop: 'static',
            centered: true,
            keyboard: false
        });
        return modalRef;
    }

    public edit(offer, modalRef) {
        this.offer.awardedPercent = this.form.controls.percent_awarded.value;
        this.offer.isAwardSelfSupply = this.form.controls.is_award_self_supply.value;
        this.offer.awardSelfSupply = this.form.controls.award_self_supply.value;
        this.offer.bid.date_contract_start = this.form.controls.date_contract_start.value;
        this.offer.bid.date_contract_end = this.form.controls.date_contract_end.value;
        this.onEdit.emit(offer);
        modalRef.close();
    }

    public getFormGroupErrorClasses(control: AbstractControl, isInvalid: boolean = false): {} {
        if (isInvalid) {
            return {'is-invalid': control.invalid};
        }
        return {'is-invalid': control.invalid && control.touched};
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control.errors && (control.dirty || control.touched);
    }

    public toNumericValue(value) {
        return parseFloat(value.toString().replace(/[^\d.-]/g, '')) || 0;
    }

    public formatVolume(value: number | string): string {
        const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
        if (isNaN(num)) return '';
        return Math.round(num).toLocaleString('en-US');
    }

    public onVolumeBlur() {
        const raw = this.form.controls.volume.value;
        this.form.controls.volume.patchValue(this.formatVolume(raw), { emitEvent: false });
    }
}
